import React, { useMemo } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Avatar, Table, Tooltip } from 'antd'
import { Icon } from '@iconify/react'

import { getRandomAvatar } from '../../../assets/clients/avatar'
import {
    getDisplayName,
    getInitials,
} from '../talent.utils'
import TalentPhoneValue from './TalentPhoneValue'
import styles from './TalentListTableView.module.css'

const TalentListTableView = ({
    users = [],
    loading = false,
    loadingMore = false,
    isNext = false,
    onLoadMore,
    onManagePermissions,
    canEdit = true,
}) => {
    const columns = useMemo(
        () => [
            {
                title: 'Talent',
                key: 'user',
                width: 280,
                render: (_, record) => {
                    const avatarSrc =
                        record?.profilePicture ||
                        getRandomAvatar({
                            seed:
                                record?._id ||
                                record?.email ||
                                record?.name,
                        })
                    return (
                        <div className={styles.userCell}>
                            <Avatar size={36} src={avatarSrc}>
                                {!avatarSrc ? getInitials(record) : null}
                            </Avatar>
                            <div className={styles.userInfo}>
                                <div className={styles.userName}>
                                    {getDisplayName(record)}
                                </div>
                                <div className={styles.userEmail}>
                                    {record?.email || 'N/A'}
                                </div>
                            </div>
                        </div>
                    )
                },
            },
            {
                title: 'Phone',
                key: 'phone',
                width: 180,
                render: (_, record) => (
                    <TalentPhoneValue
                        record={record}
                        className={styles.phoneCell}
                        muted={!record?.phone}
                    />
                ),
            },
            {
                title: 'Status',
                key: 'status',
                width: 130,
                render: (_, record) =>
                    record?.isDeleted ? (
                        <span className={styles.statusArchived}>Archived</span>
                    ) : (
                        <span className={styles.statusActive}>Active</span>
                    ),
            },
            ...(canEdit
                ? [
                      {
                          title: 'Actions',
                          key: 'actions',
                          width: 100,
                          render: (_, record) => (
                              <div className={styles.actionButtons}>
                                  <Tooltip title="Permissions">
                                      <button
                                          type="button"
                                          className={styles.iconBtn}
                                          onClick={() =>
                                              onManagePermissions?.(record)
                                          }
                                      >
                                          <Icon
                                              icon="mdi:shield-account-outline"
                                              width={16}
                                              height={16}
                                          />
                                      </button>
                                  </Tooltip>
                              </div>
                          ),
                      },
                  ]
                : []),
        ],
        [onManagePermissions, canEdit]
    )

    if (loading && !users.length) {
        return (
            <div className={styles.loadingWrap}>
                <Icon
                    icon="line-md:loading-loop"
                    style={{ fontSize: 48, color: '#6366f1' }}
                />
            </div>
        )
    }

    if (!users.length) {
        return (
            <div className={styles.emptyState}>
                <Icon icon="mdi:account-group-outline" style={{ fontSize: 48 }} />
                <div>No talent users found.</div>
            </div>
        )
    }

    return (
        <div className={styles.tableWrap}>
            <InfiniteScroll
                dataLength={users.length}
                next={onLoadMore}
                hasMore={isNext}
                initialLoad={false}
                scrollThreshold="200px"
                scrollableTarget="talent-scroll-container"
                loader={
                    loadingMore ? (
                        <div className={styles.scrollLoader}>
                            <Icon
                                icon="line-md:loading-loop"
                                style={{ fontSize: 26, color: '#6366f1' }}
                            />
                        </div>
                    ) : null
                }
            >
                <div className={styles.tableCard}>
                    <Table
                        className={styles.table}
                        rowKey={(record) =>
                            record?._id || record?.id || record?.email
                        }
                        columns={columns}
                        dataSource={users}
                        pagination={false}
                        loading={loading && users.length > 0}
                    />
                </div>
            </InfiniteScroll>
        </div>
    )
}

export default TalentListTableView
